import axios from 'axios';

const BASE_URL = 'https://payrollnodbodemo.magnussoftech.in/api';

export const attendanceService = {
  // Get current location
  getCurrentLocation: () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by your browser'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy
          });
        },
        (error) => {
          reject(new Error('Failed to get location: ' + error.message));
        },
        {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0
        }
      );
    });
  },

  // Calculate distance between two points
  calculateDistance: (lat1, lon1, lat2, lon2) => {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = (lat1 * Math.PI) / 180;
    const φ2 = (lat2 * Math.PI) / 180;
    const Δφ = ((lat2 - lat1) * Math.PI) / 180;
    const Δλ = ((lon2 - lon1) * Math.PI) / 180;

    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c; // Distance in meters
  },

  // Verify attendance
  verifyAttendance: async (image, location) => {
    try {
      const formData = new FormData();
      formData.append('title', 'VerifyAttendance');
      formData.append('description', 'Verify attendance with image and location');
      formData.append('ReqAttImage', image);
      formData.append('ReqAttLocation', JSON.stringify(location));
      formData.append('ReqCompanyCode', '1');

      const response = await axios.post(
        `${BASE_URL}/VerifyAttendance`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          timeout: 30000
        }
      );

      return response.data;
    } catch (error) {
      console.error('Error verifying attendance:', error);
      throw new Error(error.response?.data?.message || 'Failed to verify attendance');
    }
  },

  // Update daily in/out entry
  updateDailyInOutEntry: async (image, date, dateTime, location, rangeType, outdoorReason) => {
    try {
      const formData = new FormData();
      formData.append('title', 'UpdateDailyInOutEntry');
      formData.append('description', 'Update attendance entry');
      formData.append('ReqAttDate', date);
      formData.append('ReqAttDateTime', dateTime);
      formData.append('ReqAttImage', image);
      formData.append('ReqAttLocation', JSON.stringify(location));
      formData.append('ReqRangeType', rangeType);
      formData.append('ReqOutDoorReason', outdoorReason);
      formData.append('ReqCompanyCode', '1');

      const response = await axios.post(
        `${BASE_URL}/UpdateDailyInOutEntry`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          timeout: 30000
        }
      );

      return response.data;
    } catch (error) {
      console.error('Error updating attendance:', error);
      throw new Error(error.response?.data?.message || 'Failed to update attendance');
    }
  },

  // Get daily attendance list
  getDailyAttendanceList: async (date, empCode) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/GetEmployeeDailyAttList`,
        {
          title: 'GetEmployeeDailyAttList',
          description: 'Fetch attendance list',
          ReqAttDate: date,
          ReqEmpCode: empCode,
          ReqCompanyCode: '1'
        },
        {
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          timeout: 10000
        }
      );

      if (response.data && response.data[0]?.JSONData1) {
        return JSON.parse(response.data[0].JSONData1);
      }
      return [];
    } catch (error) {
      console.error('Error fetching attendance:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch attendance');
    }
  },

  // Get monthly attendance list
  getMonthlyAttendanceList: async (month, year, empCode) => {
    try {
      const response = await axios.post(BASE_URL, {
        title: 'GetEmployeeMonthlyAttList',
        ReqMonthNum: month,
        ReqYear: year,
        ReqEmpCode: empCode,
        ReqCompanyCode: localStorage.getItem('companyCode')
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Update attendance review
  updateAttendanceReview: async (attId, reviewStatus, reviewRemarks, companyCode) => {
    const API_URL = 'https://botestlegal.magnussoftech.in/API/WebServiceDailyStaffAtt.aspx';
    try {
      const response = await axios.post(API_URL, new URLSearchParams({
        title: 'UpdateInOutEntryReview',
        ReqAttID: String(attId),
        Reqreviewsts: reviewStatus,
        Reqreviewby: localStorage.getItem('employeeName') || 'Admin',
        ReqreviewRemarks: reviewRemarks,
        ReqCompanyCode: companyCode || localStorage.getItem('companyCode'),
      }), {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      });

      if (typeof response.data === 'string') {
        const jsonPart = response.data.split('||JasonEnd')[0].trim();
        const parsed = JSON.parse(jsonPart);
        return parsed;
      }
      return response.data; // Fallback in case response is not a string with delimiter
    } catch (error) {
      console.error('Error updating attendance review:', error);
      throw new Error(error.response?.data?.message || 'Failed to update attendance review');
    }
  },

  // Add this function for staff monthly attendance
  getEmployeeMonthlyAttList: async (month, year, empCode, companyCode) => {
    const API_URL = 'https://botestlegal.magnussoftech.in/API/WebServiceDailyStaffAtt.aspx';
    const formData = new URLSearchParams();
    formData.append('title', 'GetEmployeeMonthlyAttList');
    formData.append('ReqMonthNum', month);
    formData.append('ReqYear', year);
    formData.append('ReqEmpCode', empCode);
    formData.append('ReqCompanyCode', companyCode);

    const response = await axios.post(API_URL, formData, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
    });

    if (typeof response.data === 'string') {
      const jsonPart = response.data.split('||JasonEnd')[0].trim();
      const parsed = JSON.parse(jsonPart);
      if (parsed[0]?.JSONData1) {
        return JSON.parse(parsed[0].JSONData1);
      }
    }
    return [];
  },

  // Add this function for admin monthly attendance
  getAdminMonthlyAttendance: async (month, year, empCode, companyCode) => {
    const API_URL = 'https://botestlegal.magnussoftech.in/API/WebServiceDailyStaffAtt.aspx';
    const formData = new URLSearchParams();
    formData.append('title', 'GetEmployeeMonthlyAttList');
    formData.append('ReqMonthNum', month);
    formData.append('ReqYear', year);
    formData.append('ReqEmpCode', empCode);
    formData.append('ReqCompanyCode', companyCode);

    try {
      const response = await axios.post(API_URL, formData, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      });

      if (typeof response.data === 'string') {
        const jsonPart = response.data.split('||JasonEnd')[0].trim();
        const parsed = JSON.parse(jsonPart);
        if (parsed[0]?.JSONData1) {
          return JSON.parse(parsed[0].JSONData1);
        }
      }
      return [];
    } catch (error) {
      console.error('Error fetching admin monthly attendance:', error);
      throw error;
    }
  },

  // Add this function for admin daily attendance
  getAdminDailyAttendance: async (date, empCode, companyCode) => {
    const API_URL = 'https://payrollnodbodemo.magnussoftech.in/api/GetEmployeeDailyAttList';
    const formData = new URLSearchParams();
    formData.append('title', 'GetEmployeeDailyAttList');
    formData.append('description', 'Fetch attendance list');
    formData.append('ReqAttDate', date);
    formData.append('ReqEmpCode', empCode);
    formData.append('ReqCompanyCode', companyCode);

    try {
      const response = await axios.post(API_URL, formData, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      });

      if (response.data && Array.isArray(response.data) && response.data[0]?.JSONData1) {
        try {
          const parsedData = JSON.parse(response.data[0].JSONData1);
          return parsedData;
        } catch (e) {
          console.error('Error parsing JSONData1:', e);
          return [];
        }
      }
      return [];
    } catch (error) {
      console.error('Error fetching admin daily attendance:', error);
      throw error;
    }
  }
}; 